"use client";

import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface ComponentErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: string;
}

interface ComponentErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  className?: string;
  showError?: boolean; // Whether to show error details in development
}

/**
 * Error boundary specifically designed for individual dynamic components
 * Provides graceful fallback without breaking the entire application
 */
export class ComponentErrorBoundary extends Component<
  ComponentErrorBoundaryProps,
  ComponentErrorBoundaryState
> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ComponentErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Log error for debugging but don't break the app
    console.warn(
      `Component error caught in ${this.props.componentName || "unknown component"}:`,
      error,
      errorInfo
    );

    this.setState({
      errorInfo: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div
          className={cn(
            "flex h-full w-full items-center justify-center bg-muted/20 border border-dashed border-muted-foreground/20 rounded-md",
            this.props.className
          )}
        >
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            <AlertTriangle className="h-5 w-5 text-muted-foreground/60" />
            <div className="text-sm text-muted-foreground">
              {this.props.componentName
                ? `${this.props.componentName} unavailable`
                : "Component unavailable"}
            </div>
            {this.props.showError &&
              process.env.NODE_ENV === "development" &&
              this.state.error && (
              <details className="mt-2 text-xs text-muted-foreground/80">
                <summary className="cursor-pointer">Error details</summary>
                <pre className="mt-1 whitespace-pre-wrap text-left">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary wrapper for functional components
 */
export function withComponentErrorBoundary<T extends object>(
  Component: React.ComponentType<T>,
  errorBoundaryProps?: Omit<ComponentErrorBoundaryProps, "children">
) {
  return function WrappedComponent(props: T) {
    return (
      <ComponentErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ComponentErrorBoundary>
    );
  };
}
